import type { SupabaseClient } from '@supabase/supabase-js';
import { openModal } from '@vario-connect/frontend-design';
import { pinModalToOpener } from '../../lib/modal-anchor.js';

// ============================================================================
// Wiederverwendbarer OTTO-Kategorie-Picker (Modal mit Tabelle + Filter) — analog
// zum Marken-Picker. Genutzt vom Vario-Slot (beim Aufnehmen) UND vom Detail-Pane
// (Kategorie nachträglich ändern). Liefert {leaf, group}, weil die Vario-Auflösung
// otto:c:<group>::<leaf> die Gruppe braucht.
//
// Datenquelle: otto_list_listable_categories() → Leaf-Kategorien der vom Händler in
// den Einstellungen gewählten Gruppen. Menge ist klein → Filter rein clientseitig.
// ============================================================================

export interface CategoryPick { leaf: string; group: string }

interface CatRow { leaf: string; group: string }

export function openCategoryPicker(
  supabase: SupabaseClient,
  opts: { current?: string | null; onPick: (pick: CategoryPick) => void },
): void {
  const modal = openModal({
    title: 'OTTO-Kategorie wählen',
    icon: 'fa-folder-tree',
    width: 640,
    body: `
      <input id="ocp-search" class="v-input" type="search" autocomplete="off"
             placeholder="Kategorie oder Gruppe suchen…" style="width:100%;" />
      <div id="ocp-hint" class="v-text-muted" style="font-size:12px;margin:6px 0;">Lade Kategorien…</div>
      <div id="ocp-results" style="flex:0 1 340px;min-height:120px;overflow:auto;"></div>`,
    footer: `<button type="button" class="v-button" data-modal-cancel>Schließen</button>`,
    onMount: (root) => {
      // Fest auf Höhe des Knopfs, Ergebnisliste mit fester Höhe: kein Springen beim Tippen.
      pinModalToOpener(root);
      const search = root.querySelector<HTMLInputElement>('#ocp-search')!;
      const hint = root.querySelector<HTMLDivElement>('#ocp-hint')!;
      const results = root.querySelector<HTMLDivElement>('#ocp-results')!;
      search.focus();

      let all: CatRow[] = [];

      const paint = (term: string) => {
        const t = term.trim().toLowerCase();
        const filtered = t
          ? all.filter((r) => r.leaf.toLowerCase().includes(t) || r.group.toLowerCase().includes(t))
          : all;
        hint.textContent = filtered.length
          ? `${filtered.length} von ${all.length} Kategorien${t ? ' (gefiltert)' : ''}`
          : 'Keine Treffer.';
        results.innerHTML = `
          <table class="klst-detail-table" style="width:100%;">
            <thead><tr><th>Kategorie</th><th>Gruppe</th></tr></thead>
            <tbody>
              ${filtered.map((r, i) => `
                <tr data-cat-idx="${i}" style="cursor:pointer;${r.leaf === opts.current ? 'background:var(--st-success-bg);' : ''}">
                  <td>${escapeHtml(r.leaf)}${r.leaf === opts.current ? ' ✓' : ''}</td>
                  <td class="v-text-muted">${escapeHtml(r.group)}</td>
                </tr>`).join('')}
            </tbody>
          </table>`;
        results.querySelectorAll<HTMLTableRowElement>('[data-cat-idx]').forEach((tr) => {
          tr.addEventListener('click', () => {
            const pick = filtered[Number(tr.dataset['catIdx'])];
            if (pick) { opts.onPick(pick); modal.close(); }
          });
        });
      };

      void (async () => {
        const { data, error } = await supabase.rpc('otto_list_listable_categories');
        if (error) { hint.textContent = `Kategorien nicht ladbar: ${error.message}`; return; }
        all = ((data ?? []) as Array<{ category: string; category_group: string }>)
          .map((r) => ({ leaf: r.category, group: r.category_group }));
        if (all.length === 0) {
          hint.textContent = 'Keine Kategorien — in den Einstellungen Kategorie-Gruppen auswählen.';
          results.innerHTML = '';
          return;
        }
        paint('');
        let timer: ReturnType<typeof setTimeout> | null = null;
        search.addEventListener('input', () => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => paint(search.value), 150);
        });
      })();
    },
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
